import { mapStowagePlanChangeToStowagePlanDataItem } from 'src/factory/Stowage/StowageOperationFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { addNamesToRotation } from '../stowageRotationUtils';
import { PortName } from 'src/types/ViewModel/Stowage/Port/PortName';
import { BerthName } from 'src/types/ViewModel/Stowage/Port/BerthName';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { AdjustedCargoTemperature } from 'src/types/ViewModel/Stowage/AdjustedCargoTemperature';
import { BlockedTank } from 'src/types/ViewModel/Stowage/BlockedTank';

export type ComparisonVoyage = {
    voyageNumber: string;
    portCalls: RotationPortCall[];
    rogueCargoes?: RotationCargo[];
};

export const mapToComparisonVoyage = (
    incomingChanges: StowagePlanChange[],
    stowagePlan: StowagePlan,
    portCallList: RotationPortCall[],
    cargoList: RotationCargo[],
    portNames: PortName[],
    berthNames: BerthName[]
) => {
    if (!incomingChanges || !stowagePlan) return;

    const incomingChangeData = mapToChangeData(incomingChanges, stowagePlan);

    return mapChangeDataToComparisonVoyage(incomingChangeData, stowagePlan, portCallList, cargoList, portNames, berthNames);
};

export const removeExtraDataFromData = (data: string) => {
    if (!data) return;
    const parsedData = JSON.parse(data);
    if (parsedData.extraData) {
        delete parsedData.extraData;
        const json = JSON.stringify(parsedData);
        return json && json !== '{}' ? json : null;
    }
    return data;
};

const mapChangeDataToComparisonVoyage = (
    incomingChangeData: any[],
    stowagePlan: StowagePlan,
    portCallList: RotationPortCall[],
    cargoList: RotationCargo[],
    portNames: PortName[],
    berthNames: BerthName[]
) => {
    let voyages: ComparisonVoyage[] = [];
    const stowageItems: Stowage[] = [];
    const adjustedCargoTemperatures: AdjustedCargoTemperature[] = [];
    const blockedTanks: BlockedTank[] = [];

    for (const imosChangeItem of incomingChangeData.sort((a, b) => a.changeDataType - b.changeDataType)) {
        const { portCall, berthCall, cargoOperation, cargo, stowageItem, adjustedCargoTemperature, blockedTank } = extactChangeData(
            imosChangeItem,
            stowagePlan,
            portCallList,
            cargoList
        );

        if (cargo?.extraData?.isRogueCargo) {
            const existingVoyage = findOrCreateItem(voyages, { voyageNumber: cargo.voyageNumber }, 'voyageNumber', {});
            if (!existingVoyage.rogueCargoes) existingVoyage.rogueCargoes = [];
            existingVoyage.rogueCargoes.push(cargo);
            continue;
        }

        if (portCall) {
            const existingVoyage = findOrCreateItem(voyages, { voyageNumber: portCall.voyageNumber }, 'voyageNumber', {
                portCalls: [],
            });

            const existingPortCall = findOrCreateItem(existingVoyage.portCalls, portCall, 'portCallGuid', { berthCalls: [] });

            if (berthCall) {
                const existingBerthCall = findOrCreateItem(existingPortCall.berthCalls, berthCall, 'berthCallGuid', { cargoOperations: [] });

                if (cargoOperation) {
                    const existingCargoOperation = findOrCreateItem(existingBerthCall.cargoOperations, cargoOperation, 'cargoOperationGuid');

                    if (cargo) {
                        existingCargoOperation.cargo = cargo;
                    }
                }
            }
        }

        if (stowageItem) findOrCreateItem(stowageItems, stowageItem, 'stowageGuid');
        if (adjustedCargoTemperature) findOrCreateItem(adjustedCargoTemperatures, adjustedCargoTemperature, 'adjustedCargoTemperatureGuid');
        if (blockedTank) findOrCreateItem(blockedTanks, blockedTank, 'blockedTankGuid');
    }

    voyages = voyages.filter((v) => v.voyageNumber);
    voyages.forEach((voyage) => addNamesToRotation(voyage.portCalls, portNames, berthNames));
    return { voyages, stowageItems, adjustedCargoTemperatures, blockedTanks };
};

const findOrCreateItem = (list: any[], item: any, guidField: string, defaults = {}) => {
    let existing = list.find((x) => x[guidField] === item[guidField]);
    if (!existing) {
        existing = { ...item, ...defaults };
        list.push(existing);
    }
    return existing;
};

const mapToChangeData = (incomingChanges: StowagePlanChange[], stowagePlan: StowagePlan) => {
    const dataTypes = {
        [StowageChangeDataType.RotationPortCall]: { listName: 'rotation', itemKey: 'portCall', guidKey: 'portCallGuid' },
        [StowageChangeDataType.RotationBerthCall]: { listName: 'berthCalls', itemKey: 'berthCall', guidKey: 'berthCallGuid' },
        [StowageChangeDataType.RotationCargoOperation]: { listName: 'cargoOperations', itemKey: 'cargoOperation', guidKey: 'cargoOperationGuid' },
        [StowageChangeDataType.RotationCargo]: { listName: 'cargoes', itemKey: 'cargo', guidKey: 'cargoGuid' },
        [StowageChangeDataType.StowageItem]: { listName: 'stowageItems', itemKey: 'stowageItem', guidKey: 'stowageGuid' },
        [StowageChangeDataType.AdjustedCargoTemperature]: {
            listName: 'adjustedCargoTemperatures',
            itemKey: 'adjustedCargoTemperature',
            guidKey: 'adjustedCargoTemperatureGuid',
        },
        [StowageChangeDataType.BlockedTank]: { listName: 'blockedTanks', itemKey: 'blockedTank', guidKey: 'blockedTankGuid' },
    };
    return incomingChanges
        .map((item) => {
            const { listName, itemKey, guidKey } = dataTypes[item.changeDataType] || {};
            if (!listName) return null;

            const properties = item.data ? JSON.parse(item.data) : {};
            const mappedDataItem = mapStowagePlanChangeToStowagePlanDataItem(item, true);
            const incomingItem = mappedDataItem?.[itemKey];
            const existingItem = (stowagePlan[listName] as (RotationPortCall | RotationBerthCall | RotationCargoOperation | RotationCargo)[])?.find(
                (i) => i[guidKey] === item.changeDataGuid
            );

            const updatedItem = existingItem
                ? item.isDeleted
                    ? { ...existingItem, ...properties, isDeleted: true }
                    : { ...existingItem, ...properties }
                : { ...incomingItem, extraData: properties.extraData };

            updatedItem.changeDataType = item.changeDataType;
            if (item.changeDataType === StowageChangeDataType.RotationPortCall) updatedItem.berthCalls = [];
            if (item.changeDataType === StowageChangeDataType.RotationBerthCall) updatedItem.portCall = null;
            if (item.changeDataType === StowageChangeDataType.RotationCargoOperation) updatedItem.berthCall = null;

            return updatedItem;
        })
        .filter(Boolean)
        .sort((a, b) => a.changeDataType - b.changeDataType);
};

const extactChangeData = (changeItem: any, stowagePlan: StowagePlan, portCallList: RotationPortCall[], cargoList: RotationCargo[]) => {
    let portCall: any, berthCall: any, cargoOperation: any, cargo: any, stowageItem: any, blockedTank: any, adjustedCargoTemperature: any;
    const findPortCallInLists = (guid: string) =>
        stowagePlan.rotation?.find((pc) => pc.portCallGuid === guid) ?? portCallList.find((pc) => pc.portCallGuid === guid);
    const findBerthCallInLists = (guid: string) =>
        stowagePlan.berthCalls?.find((bc) => bc.berthCallGuid === guid) ??
        portCallList.flatMap((pc) => pc.berthCalls)?.find((bc) => bc.berthCallGuid === guid);
    const findCargoOperationInLists = (guid: string, checkOnCargo?: boolean) =>
        stowagePlan.cargoOperations?.find((co) => (checkOnCargo ? co.cargoGuid === guid : co.cargoOperationGuid === guid)) ??
        portCallList
            .flatMap((pc) => pc.berthCalls.flatMap((bc) => bc.cargoOperations))
            ?.find((co) => (checkOnCargo ? co.cargoGuid === guid : co.cargoOperationGuid === guid));
    const findCargoInLists = (guid: string) =>
        stowagePlan.cargoes?.find((co) => co.cargoGuid === guid) ??
        portCallList.flatMap((pc) => pc.berthCalls.flatMap((bc) => bc.cargoOperations.flatMap((co) => co.cargo)))?.find((c) => c.cargoGuid === guid);
    const findWithCargo = (list: any[]) =>
        list.find((pc) => pc.berthCalls.some((bc) => bc.cargoOperations.some((co) => co.cargoGuid === changeItem.cargoGuid)));

    switch (changeItem.changeDataType) {
        case StowageChangeDataType.RotationPortCall:
            portCall = changeItem;
            break;
        case StowageChangeDataType.RotationBerthCall:
            berthCall = changeItem;
            portCall = findPortCallInLists(changeItem.portCallGuid);
            break;
        case StowageChangeDataType.RotationCargoOperation:
            cargoOperation = changeItem;
            portCall = findPortCallInLists(changeItem.portCallGuid);
            berthCall = findBerthCallInLists(changeItem.berthCallGuid);
            cargo =
                cargoOperation.cargo ??
                cargoList.find((c) => c.cargoGuid === changeItem.cargoGuid) ??
                stowagePlan.cargoes.find((c) => c.cargoGuid === changeItem.cargoGuid);
            break;
        case StowageChangeDataType.RotationCargo:
            cargo = changeItem;
            portCall = findWithCargo(stowagePlan.rotation) ?? findWithCargo(portCallList);
            berthCall = portCall?.berthCalls.find((bc) => bc.cargoOperations.some((co) => co.cargoGuid === changeItem.cargoGuid));
            cargoOperation = berthCall?.cargoOperations.find((co) => co.cargoGuid === changeItem.cargoGuid);
            break;
        case StowageChangeDataType.StowageItem:
            stowageItem = changeItem;
            cargoOperation = findCargoOperationInLists(changeItem.cargoOperationGuid);
            berthCall = cargoOperation?.berthCall;
            portCall = berthCall?.portCall;
            cargo = cargoOperation?.cargo;
            break;
        case StowageChangeDataType.BlockedTank:
            blockedTank = changeItem;
            blockedTank.fromBerthCall = findBerthCallInLists(changeItem.fromBerthCallGuid);
            blockedTank.toBerthCall = findBerthCallInLists(changeItem.toBerthCallGuid);
            blockedTank.fromPortCall = findPortCallInLists(changeItem.fromPortCallGuid);
            blockedTank.toPortCall = findPortCallInLists(changeItem.fromPortCallGuid);

            berthCall = blockedTank.fromBerthCall;
            portCall = blockedTank.fromPortCall;
            break;
        case StowageChangeDataType.AdjustedCargoTemperature:
            adjustedCargoTemperature = changeItem;
            cargo = findCargoInLists(changeItem.cargoGuid);
            cargoOperation = findCargoOperationInLists(changeItem.cargoGuid, true);
            berthCall = cargoOperation?.berthCall;
            portCall = berthCall?.portCall;
            break;
    }

    return { portCall, berthCall, cargoOperation, cargo, stowageItem, blockedTank, adjustedCargoTemperature };
};
